/**
 * CLARTEXT · Fuehrerscheinkontrolle · Block F (Feb 2026)
 * -----------------------------------------------------
 * Owner-Seite fuer die Mandanten-Freigabe eingereichter Fuehrerschein-
 * Fotos. Strikt ADDITIV — nutzt vorhandene Anker:
 *   - Piktogramme aus Block C (`vehicleCategoryIcons.js`)
 *   - api-Wrapper `api.listOpenFskRecords/getFskRecordDetail/confirmFskRecord`
 *   - Backend-Proxy-Byte-Stream fuer Fotos (Frage 7b · analog Wirksamkeit-PDF)
 *
 * BE-003 · Frage 14a: Nach der Entscheidung wird das Foto asynchron aus dem
 * regulaeren Bucket geloescht (Retry-Queue + FSK_PHOTO_DELETED-Audit). Die
 * UI zeigt einen sichtbaren Hinweis, dass das Bild danach nicht mehr
 * verfuegbar ist.
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  AlertTriangle,
  IdCard,
  Check,
  X,
  ShieldCheck,
  ShieldAlert,
  Loader2,
  ZoomIn,
  XCircle,
  Download,
  FileArchive,
  CalendarRange,
  FileText,
  PackageCheck,
  Search,
} from "lucide-react";
import { api } from "@/lib/api";
import { getVehiclePictogramSrc } from "@/lib/vehicleCategoryIcons";

/**
 * UI-REDESIGN Ergänzung (Aug 2026, betreibergenehmigt) · Führerschein-Suche.
 *
 * Reine Helper-Funktion: filtert eine Liste von Führerschein-Records nach
 * dem Mitarbeiter-Displayname (case-insensitive Substring). Wird von der
 * Container-Komponente `UnternehmerFuehrerschein` verwendet UND separat
 * getestet (siehe `UnternehmerFuehrerschein.search.test.jsx`).
 *
 * Verhalten:
 *   - Leerer/whitespace-only Suchbegriff → Liste unverändert zurück.
 *   - Sonst: Substring-Vergleich auf `employee.display_name`, mit
 *     Fallback auf `first_name + last_name` (wie im Listen-Renderer,
 *     Z. 911-915 im Original-Code).
 *   - Records ohne Employee (Sollte nicht vorkommen, defensiv):
 *     werden nur ausgeblendet, wenn ein Suchbegriff aktiv ist.
 */
export function filterFskRecordsByName(records, searchTerm) {
  const term = (searchTerm || "").trim().toLowerCase();
  if (!term) return records;
  return (records || []).filter((r) => {
    const emp = r?.employee || {};
    const displayName =
      emp.display_name
      || `${emp.first_name || ""} ${emp.last_name || ""}`.trim()
      || "";
    return displayName.toLowerCase().includes(term);
  });
}

const DEFAULT_PLACEHOLDER = "Bitte auswählen…";

// Block F Nachtrag (Feb 2026): kleines Utility fuer Blob-Downloads (WORM-JSON
// + ZIP). Rein clientseitig — kein neuer Endpoint, keine Backend-Aenderung.
function saveBlobAs(blob, fallbackName, headers) {
  let filename = fallbackName;
  const disposition =
    (headers && (headers["content-disposition"] || headers["Content-Disposition"])) || "";
  const m = /filename\s*=\s*"?([^";]+)"?/i.exec(disposition);
  if (m && m[1]) filename = m[1];
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1500);
}

function EmptyState() {
  return (
    <div
      data-testid="fsk-empty-state"
      className="rounded-xl border border-slate-200 bg-white px-6 py-12 text-center"
    >
      <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-emerald-50">
        <ShieldCheck className="h-7 w-7 text-emerald-600" />
      </div>
      <h3 className="text-base font-semibold text-slate-900">
        Keine offenen Führerschein-Prüfungen.
      </h3>
      <p className="mt-1 text-sm text-slate-600">
        Neue Einreichungen Ihrer Fahrer erscheinen hier automatisch.
      </p>
    </div>
  );
}

function CategoryChips({ categories }) {
  if (!categories?.length) return null;
  return (
    <div
      data-testid="fsk-record-categories"
      className="flex flex-wrap gap-2"
    >
      {categories.map((cat) => (
        <div
          key={cat.identifier}
          data-testid={`fsk-record-category-chip-${cat.identifier}`}
          className="inline-flex items-center gap-2 rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-800"
        >
          <img
            src={getVehiclePictogramSrc(cat.pictogram_key)}
            alt={cat.pictogram_alt_text || cat.display_name}
            className="h-5 w-5 object-contain"
          />
          <span>{cat.display_name}</span>
        </div>
      ))}
    </div>
  );
}

function DecisionResultBanner({ record }) {
  if (record.mandant_decision === "offen") return null;
  const isOk = record.mandant_decision === "freigegeben";
  return (
    <div
      data-testid="fsk-record-decision-result"
      className={
        isOk
          ? "rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm text-emerald-900"
          : "rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-900"
      }
    >
      <div className="flex items-center gap-2 font-semibold">
        {isOk ? <ShieldCheck className="h-4 w-4" /> : <ShieldAlert className="h-4 w-4" />}
        {isOk ? "Freigegeben" : "Abgelehnt"}
      </div>
      <div className="text-xs mt-0.5">
        {record.mandant_decision_at
          ? new Date(record.mandant_decision_at).toLocaleString("de-DE")
          : ""}
      </div>
      {/* Block G (Feb 2026 · §11.c/§4.5): Wiedervorlage-Datum unter dem
          Entscheidungs-Zeitstempel. Nur bei Freigabe befuellt. */}
      {isOk && record.wiedervorlage_faellig_am ? (
        <div
          className="text-xs mt-0.5"
          data-testid="fsk-record-detail-due"
        >
          Wiedervorlage fällig am{" "}
          {new Date(record.wiedervorlage_faellig_am).toLocaleDateString("de-DE")}
        </div>
      ) : null}
      {record.mandant_decision_note ? (
        <div className="mt-1 text-xs italic">
          „{record.mandant_decision_note}&ldquo;
        </div>
      ) : null}
    </div>
  );
}

function PhotoBlock({ recordId, side, available, deletedAt, isLegacy, onZoom }) {
  const [blobUrl, setBlobUrl] = useState("");
  const [loadError, setLoadError] = useState(false);

  useEffect(() => {
    // BUGFIX 2026-08-09: <img src="{API}/…/photo/…"> schickte keinen Bearer
    // (Browser umgeht den axios-Interceptor) → 401. Fix: Bytes explizit
    // ueber axios (mit Auth-Interceptor) als Blob laden und via
    // URL.createObjectURL binden. Kein Endpoint-Wechsel — Frage 7b bleibt.
    let cancelled = false;
    let localUrl = "";
    setLoadError(false);
    setBlobUrl("");
    if (!available || deletedAt) return () => {};
    api
      .fetchFskPhotoBlob(recordId, side)
      .then((blob) => {
        if (cancelled) return;
        localUrl = URL.createObjectURL(blob);
        setBlobUrl(localUrl);
      })
      .catch(() => {
        if (!cancelled) setLoadError(true);
      });
    return () => {
      cancelled = true;
      if (localUrl) URL.revokeObjectURL(localUrl);
    };
  }, [recordId, side, available, deletedAt]);

  if (deletedAt) {
    return (
      <div
        data-testid={`fsk-photo-deleted-${side}`}
        className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-center text-xs text-slate-600"
      >
        Foto wurde nach der Entscheidung gelöscht (Datenminimierung).
      </div>
    );
  }
  if (!available) {
    if (isLegacy && side === "back") {
      return (
        <div
          data-testid="fsk-photo-legacy-back-hint"
          className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-center text-xs text-slate-600"
        >
          Alt-Datensatz — nur Vorderseite verfügbar.
        </div>
      );
    }
    return null;
  }
  if (loadError) {
    return (
      <div
        data-testid={`fsk-photo-error-${side}`}
        className="rounded-lg border border-rose-200 bg-rose-50 p-4 text-center text-xs text-rose-800"
      >
        Foto konnte nicht geladen werden.
      </div>
    );
  }
  if (!blobUrl) {
    return (
      <div
        data-testid={`fsk-photo-loading-${side}`}
        className="rounded-lg border border-slate-200 bg-slate-50 p-8 text-center text-xs text-slate-500"
      >
        <Loader2 className="mx-auto mb-2 h-4 w-4 animate-spin" />
        Lädt…
      </div>
    );
  }
  return (
    <button
      type="button"
      onClick={() => onZoom?.({ src: blobUrl, side })}
      data-testid={`fsk-photo-zoom-${side}`}
      className="group relative block w-full overflow-hidden rounded-lg border border-slate-200 bg-slate-50 cursor-zoom-in focus:outline-none focus:ring-2 focus:ring-slate-500"
      aria-label={
        side === "front"
          ? "Vorderseite vergrößert anzeigen"
          : "Rückseite vergrößert anzeigen"
      }
    >
      <img
        data-testid={`fsk-photo-${side}`}
        src={blobUrl}
        alt={side === "front" ? "Vorderseite" : "Rückseite"}
        className="w-full object-contain max-h-[420px]"
      />
      <span className="pointer-events-none absolute right-2 top-2 inline-flex items-center gap-1 rounded-full bg-slate-900/70 px-2 py-1 text-[11px] text-white opacity-0 transition-opacity group-hover:opacity-100">
        <ZoomIn className="h-3 w-3" />
        Vergrößern
      </span>
    </button>
  );
}


function PhotoLightbox({ open, src, side, onClose }) {
  // Reine Darstellung — keine Backend-Aenderung, keine neuen Bytes.
  // ESC schliesst; Klick auf Backdrop schliesst; Klick auf Bild macht nichts.
  useEffect(() => {
    if (!open) return () => {};
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    document.addEventListener("keydown", onKey);
    // Body-Scroll sperren waehrend Lightbox
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose]);

  if (!open || !src) return null;
  return (
    <div
      data-testid="fsk-photo-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label={side === "front" ? "Vorderseite Großansicht" : "Rückseite Großansicht"}
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
    >
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onClose?.();
        }}
        data-testid="fsk-photo-lightbox-close"
        className="absolute right-4 top-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-sm font-medium text-white hover:bg-white/20 transition-colors"
        aria-label="Großansicht schließen"
      >
        <XCircle className="h-4 w-4" />
        Schließen
      </button>
      <img
        data-testid="fsk-photo-lightbox-image"
        src={src}
        alt={side === "front" ? "Vorderseite Großansicht" : "Rückseite Großansicht"}
        onClick={(e) => e.stopPropagation()}
        className="max-h-[90vh] max-w-[95vw] rounded-lg shadow-2xl object-contain cursor-default"
      />
    </div>
  );
}

function RecordDetail({ recordSummary, onDecided }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  // Lightbox: {src, side} oder null
  const [lightbox, setLightbox] = useState(null);

  useEffect(() => {
    if (!recordSummary?.id) return;
    setLoading(true);
    setError("");
    setNote("");
    api
      .getFskRecordDetail(recordSummary.id)
      .then((d) => setDetail(d))
      .catch((e) => setError(e?.response?.data?.detail || "Detail konnte nicht geladen werden."))
      .finally(() => setLoading(false));
  }, [recordSummary?.id]);

  const isDecided = detail?.mandant_decision && detail.mandant_decision !== "offen";
  const canDecide = !!detail && !isDecided && !busy;

  const submit = async (decision) => {
    if (!detail) return;
    if (decision === "abgelehnt" && note.trim().length < 3) {
      toast.error("Bitte einen kurzen Grund (mind. 3 Zeichen) für die Ablehnung angeben.");
      return;
    }
    setBusy(true);
    try {
      const res = await api.confirmFskRecord(detail.id, decision, note.trim());
      toast.success(
        decision === "freigegeben"
          ? "Freigabe dokumentiert. Foto wird jetzt gelöscht."
          : "Ablehnung dokumentiert. Foto wird jetzt gelöscht."
      );
      const updated = await api.getFskRecordDetail(detail.id);
      setDetail(updated);
      onDecided?.(res);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  if (loading || !recordSummary) {
    return (
      <div
        data-testid="fsk-record-detail-loading"
        className="rounded-xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-500"
      >
        <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
        Details werden geladen…
      </div>
    );
  }
  if (error) {
    return (
      <div className="rounded-xl border border-rose-200 bg-rose-50 p-6 text-sm text-rose-900">
        {error}
      </div>
    );
  }
  if (!detail) return null;

  return (
    <div
      data-testid={`fsk-record-detail-${detail.id}`}
      className="rounded-xl border border-slate-200 bg-white p-6"
    >
      <div className="flex items-start justify-between gap-4 mb-4">
        <div className="min-w-0">
          <div className="text-xs uppercase tracking-widest text-slate-400">
            Führerschein-Prüfung
          </div>
          <div
            className="mt-1 truncate font-heading text-xl font-semibold text-slate-900"
            data-testid="fsk-record-detail-employee-name"
          >
            {detail.employee?.display_name
              || [detail.employee?.first_name, detail.employee?.last_name]
                   .filter(Boolean).join(" ")
              || "Unbekannter Mitarbeiter"}
          </div>
          <div
            className="mt-0.5 text-[11px] text-slate-400 font-mono truncate"
            data-testid="fsk-record-detail-record-id"
            title="Interne Record-ID (technische Nachvollziehbarkeit)"
          >
            Record-ID: {detail.id}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            Eingereicht am {detail.declaration?.accepted_at
              ? new Date(detail.declaration.accepted_at).toLocaleString("de-DE")
              : "—"}
          </div>
        </div>
      </div>

      <DecisionResultBanner record={detail} />

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <PhotoBlock
          recordId={detail.id}
          side="front"
          available={detail.photo?.front_available}
          deletedAt={detail.photo?.deleted_at}
          isLegacy={false}
          onZoom={setLightbox}
        />
        <PhotoBlock
          recordId={detail.id}
          side="back"
          available={detail.photo?.back_available}
          deletedAt={detail.photo?.deleted_at}
          isLegacy={!!detail.photo?.is_legacy_single_side}
          onZoom={setLightbox}
        />
      </div>

      <PhotoLightbox
        open={!!lightbox}
        src={lightbox?.src || ""}
        side={lightbox?.side || "front"}
        onClose={() => setLightbox(null)}
      />

      <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <div data-testid="fsk-record-assigned-classes-panel">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">
            Zugewiesene Fahrzeugkategorien
          </div>
          <CategoryChips categories={detail.assigned_categories} />
        </div>
        <div data-testid="fsk-record-ocr-classes-panel">
          <div className="text-xs uppercase tracking-widest text-slate-400 mb-2">
            KI-Klassen aus dem Führerschein (unverbindlich)
          </div>
          {detail.ocr?.licence_classes?.length ? (
            <div
              data-testid="fsk-record-ocr-classes-list"
              className="flex flex-wrap gap-2"
            >
              {detail.ocr.licence_classes.map((cls) => (
                <span
                  key={cls}
                  data-testid={`fsk-record-ocr-class-chip-${cls}`}
                  className="inline-flex items-center rounded-full border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-800"
                >
                  {cls}
                </span>
              ))}
            </div>
          ) : (
            <div
              data-testid="fsk-record-ocr-classes-empty"
              className="text-xs italic text-slate-500"
            >
              Keine KI-Auswertung verfügbar.
            </div>
          )}
        </div>
      </div>

      {detail.ocr && (detail.ocr.full_name || detail.ocr.expiry_date) ? (
        <div
          data-testid="fsk-record-ocr"
          className="mt-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-xs text-slate-700"
        >
          <div className="text-slate-500 mb-1 uppercase tracking-widest text-[10px]">
            OCR-Lesehilfe (unverbindlich)
          </div>
          {detail.ocr.full_name ? <div>Name (KI): {detail.ocr.full_name}</div> : null}
          {detail.ocr.expiry_date ? <div>Ablaufdatum (KI): {detail.ocr.expiry_date}</div> : null}
          <div className="mt-1 text-[11px] italic text-slate-500">
            KI-Auswertung — der Mandant entscheidet weiterhin allein.
          </div>
        </div>
      ) : null}

      <div className="mt-4">
        <div className="whitespace-pre-wrap rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-700">
          {detail.declaration?.body || ""}
        </div>
      </div>

      {!isDecided ? (
        <>
          <div className="mt-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
            <div>
              Nach Ihrer Entscheidung wird das Foto automatisch gelöscht
              (Datenminimierung). Der Nachweis der Prüfung bleibt 10 Jahre
              erhalten.
            </div>
          </div>
          <div className="mt-4">
            <label
              htmlFor="fsk-decision-note"
              className="mb-1 block text-xs font-medium text-slate-600"
            >
              Notiz (bei Ablehnung Pflicht, mind. 3 Zeichen)
            </label>
            <textarea
              id="fsk-decision-note"
              data-testid="fsk-decision-note-input"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              placeholder="Optional: Anmerkungen zur Entscheidung…"
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-500 focus:outline-none"
            />
          </div>
          <div className="mt-4 flex items-center gap-3">
            <button
              type="button"
              onClick={() => submit("freigegeben")}
              disabled={!canDecide}
              data-testid="fsk-decision-freigeben-button"
              className="inline-flex items-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <Check className="h-4 w-4" />
              Freigeben
            </button>
            <button
              type="button"
              onClick={() => submit("abgelehnt")}
              disabled={!canDecide}
              data-testid="fsk-decision-ablehnen-button"
              className="inline-flex items-center gap-2 rounded-md border border-rose-300 bg-white px-4 py-2 text-sm font-medium text-rose-700 hover:bg-rose-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <X className="h-4 w-4" />
              Ablehnen
            </button>
            {busy ? <Loader2 className="h-4 w-4 animate-spin text-slate-500" /> : null}
          </div>
        </>
      ) : (
        <div className="mt-4 flex flex-wrap items-center gap-3">
          <button
            type="button"
            data-testid="fsk-record-pdf-download-button"
            onClick={async () => {
              try {
                const { blob, headers } = await api.fetchFskNachweisPdfBlob(detail.id);
                saveBlobAs(blob, `${detail.id}.pdf`, headers);
                toast.success("PDF-Nachweis heruntergeladen.");
              } catch (e) {
                toast.error(
                  e?.response?.data?.detail || "Download fehlgeschlagen."
                );
              }
            }}
            className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors"
          >
            <FileText className="h-4 w-4" />
            PDF herunterladen
          </button>
          <button
            type="button"
            data-testid="fsk-record-worm-download-button"
            onClick={async () => {
              try {
                const { blob, headers } = await api.fetchFskWormJsonBlob(detail.id);
                saveBlobAs(blob, `${detail.id}.json`, headers);
                toast.success("WORM-Rohdatei (JSON) heruntergeladen.");
              } catch (e) {
                toast.error(
                  e?.response?.data?.detail || "Download fehlgeschlagen."
                );
              }
            }}
            className="text-xs text-slate-500 underline decoration-dotted underline-offset-4 hover:text-slate-800"
            title="Byte-identische WORM-Rohdatei (JSON) – für technische Nachvollziehbarkeit"
          >
            WORM-Rohdatei (JSON)
          </button>
        </div>
      )}
    </div>
  );
}

export default function UnternehmerFuehrerschein() {
  const [activeTab, setActiveTab] = useState("open"); // "open" | "decided"
  const [openRecords, setOpenRecords] = useState([]);
  const [decidedRecords, setDecidedRecords] = useState([]);
  const [decidedCount, setDecidedCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedIdOpen, setSelectedIdOpen] = useState(null);
  const [selectedIdDecided, setSelectedIdDecided] = useState(null);
  const [zipBusy, setZipBusy] = useState(false);
  // Zeitraum-Filter (Frage S1)
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");
  // UI-REDESIGN Ergänzung (Aug 2026, betreibergenehmigt) · tabübergreifende
  // Mitarbeitersuche. Ein gemeinsamer State über beide Tabs — der Suchbegriff
  // bleibt beim Tab-Wechsel erhalten und filtert die jeweilige aktive Liste.
  // Filter-Logik siehe `filterFskRecordsByName` (oben in dieser Datei),
  // rein clientseitig, kein neuer Endpoint.
  const [searchTerm, setSearchTerm] = useState("");
  // UI-REDESIGN Ergänzung (Aug 2026, Nachbesserung II · betreibergenehmigt):
  // Bei aktivem Suchbegriff wird eine gemeinsame Trefferliste aus Offenen
  // UND Abgeschlossenen Records angezeigt — der Betreiber muss nicht mehr
  // Tabs wechseln, um alle Treffer zu sehen. Selection bekommt einen dritten,
  // unabhängigen Cursor `selectedIdSearch` (bestehende `selectedIdOpen`/
  // `selectedIdDecided` bleiben für den Tab-Modus unangetastet).
  const [selectedIdSearch, setSelectedIdSearch] = useState(null);
  // Block H (Feb 2026 · §11.i): Zahlstatus des Fahrer-Add-ons fuer den
  // Hinweisbanner (Sidebar-Gate liegt in UnternehmerLayout, NICHT hier).
  const [addonStatus, setAddonStatus] = useState(null);

  const activeRecords = activeTab === "open" ? openRecords : decidedRecords;

  // UI-REDESIGN Ergänzung (Aug 2026, Nachbesserung II): kombinierte Liste
  // aus offenen + abgeschlossenen Records mit `_bucket`-Marker
  // ("open" | "decided") für Status-Badge + Zähler-Split. Sortierung: neuester
  // Datensatz zuerst (decided_at || mandant_decision_at || submitted_at,
  // absteigend); bei fehlendem Datum ans Ende.
  const combinedRecords = useMemo(() => {
    const bucketed = [
      ...openRecords.map((r) => ({ ...r, _bucket: "open" })),
      ...decidedRecords.map((r) => ({ ...r, _bucket: "decided" })),
    ];
    bucketed.sort((a, b) => {
      const ad = String(a.decided_at || a.mandant_decision_at || a.submitted_at || "");
      const bd = String(b.decided_at || b.mandant_decision_at || b.submitted_at || "");
      return bd.localeCompare(ad);
    });
    return bucketed;
  }, [openRecords, decidedRecords]);

  const searchActive = searchTerm.trim() !== "";

  // Effektive Sichtbarkeit: Bei aktiver Suche → kombinierte Trefferliste
  // (open + decided vermischt). Bei leerer Suche → aktuelle Tab-Liste
  // (Verhalten unverändert wie vor der Nachbesserung).
  // In beiden Fällen wird `_bucket` gesetzt, damit der Renderer einheitlich
  // arbeiten kann (kein activeTab-Zweig mehr in der Listen-Karte).
  const visibleRecords = useMemo(() => {
    if (searchActive) {
      return filterFskRecordsByName(combinedRecords, searchTerm);
    }
    const augmented = activeRecords.map((r) => ({ ...r, _bucket: activeTab }));
    return filterFskRecordsByName(augmented, searchTerm);
  }, [searchActive, combinedRecords, activeRecords, activeTab, searchTerm]);

  // Rückwärts-Kompatibilität: `filteredActiveRecords` bleibt als Test-
  // Anker erhalten (verweist auf `visibleRecords`, die inhaltlich dieselbe
  // Rolle übernimmt).
  const filteredActiveRecords = visibleRecords;

  // Zähler pro Bucket unter aktiver Suche (für Tab-Beschriftung "0/2" etc.)
  const searchBucketCounts = useMemo(() => {
    if (!searchActive) return { open: 0, decided: 0 };
    return {
      open: visibleRecords.filter((r) => r._bucket === "open").length,
      decided: visibleRecords.filter((r) => r._bucket === "decided").length,
    };
  }, [searchActive, visibleRecords]);

  const selectedId = searchActive
    ? selectedIdSearch
    : activeTab === "open"
      ? selectedIdOpen
      : selectedIdDecided;
  const setSelectedId = searchActive
    ? setSelectedIdSearch
    : activeTab === "open"
      ? setSelectedIdOpen
      : setSelectedIdDecided;

  const loadOpen = () => {
    setLoading(true);
    api
      .listOpenFskRecords()
      .then((rows) => {
        setOpenRecords(rows || []);
        if (rows?.length) {
          setSelectedIdOpen((prev) =>
            prev && rows.find((r) => r.id === prev) ? prev : rows[0].id
          );
        } else {
          setSelectedIdOpen(null);
        }
      })
      .catch(() => {
        setOpenRecords([]);
        setSelectedIdOpen(null);
      })
      .finally(() => setLoading(false));
  };

  const loadDecided = (params = {}) => {
    setLoading(true);
    const q = {};
    if (params.since) q.since = params.since;
    if (params.until) q.until = params.until;
    Promise.all([
      api.listDecidedFskRecords(q),
      api.countDecidedFskRecords(q),
    ])
      .then(([rows, cnt]) => {
        setDecidedRecords(rows || []);
        setDecidedCount(Number(cnt?.decided || 0));
        if (rows?.length) {
          setSelectedIdDecided((prev) =>
            prev && rows.find((r) => r.id === prev) ? prev : rows[0].id
          );
        } else {
          setSelectedIdDecided(null);
        }
      })
      .catch((e) => {
        setDecidedRecords([]);
        setSelectedIdDecided(null);
        toast.error(
          e?.response?.data?.detail || "Historie konnte nicht geladen werden."
        );
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    // Block H: Add-on-Zahlstatus fuer den Hinweisbanner (best-effort).
    // MODUL-REITER-02, Betreiberfreigabe 29.10.2026: Status zuerst; Fehler beim Status → Laden wie bisher.
    api
      .getModuleAddonStatus("fuehrerschein")
      .then(setAddonStatus)
      .catch(() => setAddonStatus({}));
  }, []);

  useEffect(() => {
    // MODUL-REITER-02, Betreiberfreigabe 29.10.2026: ohne aktives Modul keine Datenabrufe.
    if (!addonStatus || addonStatus.module_active === false) return;
    // Beim ersten Mount die offene Liste + den decided-Zaehler laden
    loadOpen();
    api
      .countDecidedFskRecords()
      .then((r) => setDecidedCount(Number(r?.decided || 0)))
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addonStatus]);

  useEffect(() => {
    // Decided-Liste laden, sobald sie sichtbar wird — entweder durch
    // Decided-Tab-Wechsel ODER durch Aktivierung des Suchmodus (Suche geht
    // tabübergreifend über open+decided und braucht die Decided-Basisliste
    // auch dann, wenn der Owner gerade im Offen-Tab steht).
    if (activeTab === "decided" || searchActive) {
      loadDecided({ since, until });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchActive]);

  const applyFilter = () => loadDecided({ since, until });
  const resetFilter = () => {
    setSince("");
    setUntil("");
    loadDecided({});
  };

  const selected = useMemo(() => {
    if (!selectedId) return null;
    // UI-REDESIGN Ergänzung (Aug 2026, Nachbesserung II): der Cursor kann
    // auf offene ODER decided-Records zeigen — daher wird die Suche über
    // `combinedRecords` geführt, damit das Detail-Panel modeunabhängig
    // funktioniert (Suche + Tab).
    return combinedRecords.find((r) => r.id === selectedId) || null;
  }, [combinedRecords, selectedId]);

  // Block H (Feb 2026 · Bericht §8): Banner-Variante aus dem Add-on-Status.
  // Modul aktiv + kein positiver Preis → "price_missing"; Preis konfiguriert,
  // aber (noch) kein aktives/trial Abo → "subscription_inactive"; sonst kein
  // Banner. Fail-closed: ohne geladenen Status kein Banner.
  const addonBanner = (() => {
    if (!addonStatus || !addonStatus.module_active) return null;
    // Kostenlos-Freigabe (0 € bewusst konfiguriert, kein Abo): kein Zahlstatus-Banner.
    if (addonStatus.amount_net_cents === 0) return null;
    if (!addonStatus.price_configured) return "price_missing";
    const st = addonStatus.subscription_status;
    if (st !== "trial" && st !== "active") return "subscription_inactive";
    return null;
  })();

  const handleDecided = () => {
    // Nach einer Entscheidung im Offen-Tab: Liste + Zaehler neu.
    loadOpen();
    api
      .countDecidedFskRecords()
      .then((r) => setDecidedCount(Number(r?.decided || 0)))
      .catch(() => {});
  };

  const downloadZip = async () => {
    setZipBusy(true);
    try {
      const params = {};
      if (since) params.since = since;
      if (until) params.until = until;
      const { blob, headers } = await api.fetchFskDecidedWormZipBlob(params);
      const fallback = "FSK-Pruefpaket.zip";
      saveBlobAs(blob, fallback, headers);
      // Nachtrag II (2026-08-09): Backend liefert `X-FSK-Package-PDF-Errors`
      // im Header. Wenn > 0, muss der Mandant sofort erfahren, dass
      // einzelne PDFs im Paket fehlen (statt still im Manifest zu stehen).
      const rawErrors =
        headers?.["x-fsk-package-pdf-errors"] ??
        headers?.["X-FSK-Package-PDF-Errors"];
      const pdfErrors = Number(rawErrors || 0);
      if (pdfErrors > 0) {
        toast.warning(
          `${pdfErrors} PDF-Nachweis${pdfErrors === 1 ? "" : "e"} konnte${
            pdfErrors === 1 ? "" : "n"
          } nicht erzeugt werden. Details siehe _manifest.json im ZIP.`,
          { duration: 8000 }
        );
      } else {
        toast.success("Führerschein-Prüfpaket heruntergeladen.");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "ZIP-Download fehlgeschlagen.");
    } finally {
      setZipBusy(false);
    }
  };

  // MODUL-REITER-02, Betreiberfreigabe 29.10.2026: ohne aktives Modul nur der Hinweis (gleiche Optik wie Aushang).
  if (addonStatus?.module_active === false) {
    return (
      <div
        className="border border-amber-200 bg-amber-50 text-amber-900 rounded-md p-4 text-sm flex flex-wrap items-center justify-between gap-3"
        data-testid="fsk-modul-nicht-gebucht"
      >
        <span>Dieses Zusatzmodul ist für Ihren Betrieb nicht gebucht.</span>
        <a
          href="/unternehmer/einstellungen#zusatzmodule"
          className="inline-flex items-center rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 transition-colors"
          data-testid="fsk-hinzubuchen-link"
        >
          Hinzubuchen
        </a>
      </div>
    );
  }

  return (
    <div data-testid="fsk-owner-page" className="space-y-6">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-widest text-slate-400">
            Führerschein Dokumentation
          </div>
          <h1 className="mt-1 font-heading text-2xl font-bold text-slate-900">
            Führerschein-Prüfungen freigeben
          </h1>
          <p className="mt-1 text-sm text-slate-600 max-w-2xl">
            Ihre Fahrer haben Fotos ihres Führerscheins eingereicht. Prüfen Sie
            Vorder- und Rückseite und entscheiden Sie aktiv über die Freigabe.
            Nach der Entscheidung wird das Foto automatisch gelöscht; der
            Nachweis Ihrer Prüfung bleibt erhalten.
          </p>
        </div>
        <button
          type="button"
          onClick={downloadZip}
          disabled={zipBusy || decidedCount === 0}
          data-testid="fsk-decided-zip-download"
          className="inline-flex items-center gap-2 rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shrink-0"
        >
          {zipBusy ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <PackageCheck className="h-4 w-4" />
          )}
          Führerschein-Prüfpaket herunterladen
        </button>
      </div>

      {/* Block H (Feb 2026): Zahlstatus-Hinweisbanner Fahrer-Add-on (§11.i/E9:
          Sidebar gated auf Modul, Zahlstatus NUR hier als Hinweis). */}
      {addonBanner && (
        <div
          data-testid="fsk-addon-billing-banner"
          data-variant={addonBanner}
          className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-amber-700 shrink-0 mt-0.5" />
            <div className="flex-1 text-amber-900">
              {addonBanner === "price_missing" ? (
                <>
                  <span className="font-medium">
                    Fahrer-Add-on noch nicht gebucht:
                  </span>{" "}
                  Die Führerscheinkontrolle ist für Ihr Unternehmen freigeschaltet,
                  es ist aber noch kein Add-on-Preis konfiguriert — die Abrechnung
                  ist noch nicht aktiv. Bei Fragen wenden Sie sich bitte an den
                  CLartext-Support.
                </>
              ) : (
                <>
                  <span className="font-medium">Add-on-Abo noch nicht aktiv:</span>{" "}
                  Der Preis für das Fahrer-Add-on ist konfiguriert, das zugehörige
                  Abo ist aber noch nicht aktiv. Bei Fragen wenden Sie sich bitte an
                  den CLartext-Support.
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* UI-REDESIGN Ergänzung (Aug 2026, betreibergenehmigt) · Mitarbeitersuche.
          Position bewusst OBERHALB der Tab-Leiste, damit die tab-übergreifende
          Wirkung visuell signalisiert wird. Filter ist rein clientseitig
          (kein neuer Endpoint), wirkt auf beide Tabs (Offen / Abgeschlossen). */}
      <div className="relative" data-testid="fsk-search-wrap">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Mitarbeiter suchen…"
          className="w-full pl-9 pr-3 py-2 rounded-md border border-slate-300 bg-white text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-slate-900 focus:border-slate-900"
          data-testid="fsk-search-input"
        />
      </div>

      {/* Tabs — Offene Prüfungen / Abgeschlossene Prüfungen */}
      <div
        className="flex items-center gap-2 border-b border-slate-200"
        data-testid="fsk-tabs"
      >
        <button
          type="button"
          data-testid="fsk-tab-open"
          onClick={() => {
            // UI-REDESIGN Ergänzung (Aug 2026, Nachbesserung II): Klick auf
            // einen Tab verlässt den Suchmodus (Suche wird geleert) und
            // wechselt in den Tab-Modus.
            if (searchActive) setSearchTerm("");
            setActiveTab("open");
          }}
          disabled={searchActive}
          aria-disabled={searchActive}
          className={
            "px-4 py-2 -mb-px text-sm font-medium transition-colors border-b-2 " +
            (searchActive
              ? "border-transparent text-slate-300 cursor-not-allowed"
              : activeTab === "open"
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-800")
          }
        >
          Offene Prüfungen ({
            searchActive
              ? `${searchBucketCounts.open}/${openRecords.length}`
              : openRecords.length
          })
        </button>
        <button
          type="button"
          data-testid="fsk-tab-decided"
          onClick={() => {
            if (searchActive) setSearchTerm("");
            setActiveTab("decided");
          }}
          disabled={searchActive}
          aria-disabled={searchActive}
          className={
            "px-4 py-2 -mb-px text-sm font-medium transition-colors border-b-2 " +
            (searchActive
              ? "border-transparent text-slate-300 cursor-not-allowed"
              : activeTab === "decided"
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-800")
          }
        >
          Abgeschlossene Prüfungen ({
            searchActive
              ? `${searchBucketCounts.decided}/${decidedCount}`
              : decidedCount
          })
        </button>
      </div>

      {activeTab === "decided" && !searchActive ? (
        <div
          className="rounded-xl border border-slate-200 bg-white p-4 flex flex-wrap items-end gap-3"
          data-testid="fsk-decided-toolbar"
        >
          <div className="flex items-center gap-2 text-slate-500 text-xs">
            <CalendarRange className="h-4 w-4" />
            Zeitraum
          </div>
          <label className="flex flex-col text-[11px] text-slate-500">
            Von
            <input
              data-testid="fsk-filter-since"
              type="date"
              value={since}
              onChange={(e) => setSince(e.target.value)}
              className="mt-0.5 rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-slate-500 focus:outline-none"
            />
          </label>
          <label className="flex flex-col text-[11px] text-slate-500">
            Bis
            <input
              data-testid="fsk-filter-until"
              type="date"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
              className="mt-0.5 rounded-md border border-slate-300 px-2 py-1.5 text-sm focus:border-slate-500 focus:outline-none"
            />
          </label>
          <button
            type="button"
            onClick={applyFilter}
            data-testid="fsk-filter-apply"
            className="rounded-md bg-slate-900 px-3 py-1.5 text-sm font-medium text-white hover:bg-slate-800"
          >
            Anwenden
          </button>
          <button
            type="button"
            onClick={resetFilter}
            data-testid="fsk-filter-reset"
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            Zurücksetzen
          </button>
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-1 space-y-2" data-testid="fsk-record-list">
          {loading ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-center text-sm text-slate-500">
              <Loader2 className="mx-auto mb-2 h-5 w-5 animate-spin" />
              Lädt…
            </div>
          ) : searchTerm.trim() && filteredActiveRecords.length === 0 ? (
            // UI-REDESIGN Ergänzung (Aug 2026, Nachbesserung): sobald ein
            // Suchbegriff aktiv ist UND das gefilterte Ergebnis leer ist,
            // wird IMMER der Filter-Empty-State (mit Suchbegriff-Text +
            // Reset-Button) gezeigt — unabhängig davon, ob die Basisliste
            // des Tabs leer wäre. So versteht der Owner sofort, dass der
            // Suchbegriff die Ursache ist, nicht ein leerer Datenbestand.
            <div
              data-testid="fsk-search-empty"
              className="rounded-xl border border-slate-200 bg-white px-6 py-8 text-center text-sm text-slate-500 space-y-2"
            >
              <div>
                Keine Treffer für „{searchTerm.trim()}&ldquo;.
              </div>
              <button
                type="button"
                onClick={() => setSearchTerm("")}
                data-testid="fsk-search-reset"
                className="text-slate-700 underline decoration-dotted underline-offset-4 hover:text-slate-900"
              >
                Filter zurücksetzen
              </button>
            </div>
          ) : !searchActive && activeRecords.length === 0 ? (
            activeTab === "open" ? (
              <EmptyState />
            ) : (
              <div
                data-testid="fsk-decided-empty"
                className="rounded-xl border border-slate-200 bg-white px-6 py-12 text-center text-sm text-slate-500"
              >
                Keine entschiedenen Prüfungen im ausgewählten Zeitraum.
              </div>
            )
          ) : (
            filteredActiveRecords.map((r) => {
              const active = selectedId === r.id;
              const displayName =
                r.employee?.display_name
                || [r.employee?.first_name, r.employee?.last_name]
                     .filter(Boolean).join(" ")
                || "Unbekannter Mitarbeiter";
              const empId = r.employee?.id || "";
              const decidedLabel =
                r._bucket === "decided" && r.mandant_decision_at
                  ? `${
                      r.mandant_decision === "freigegeben" ? "Freigegeben" : "Abgelehnt"
                    }: ${new Date(r.mandant_decision_at).toLocaleDateString("de-DE")}`
                  : r.declaration_accepted_at
                  ? `Eingereicht: ${new Date(r.declaration_accepted_at).toLocaleDateString("de-DE")}`
                  : "";
              return (
                <button
                  type="button"
                  key={r.id}
                  onClick={() => setSelectedId(r.id)}
                  data-testid={`fsk-record-list-item-${r.id}`}
                  className={
                    active
                      ? "w-full rounded-xl border border-slate-900 bg-slate-900 px-4 py-3 text-left text-white transition-colors"
                      : "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-slate-800 hover:border-slate-400 transition-colors"
                  }
                >
                  <div className="flex items-center gap-2">
                    <IdCard className={active ? "h-4 w-4 text-white" : "h-4 w-4 text-slate-500"} />
                    <div
                      className="text-sm font-semibold truncate"
                      data-testid={`fsk-record-list-item-name-${r.id}`}
                    >
                      {displayName}
                    </div>
                    {/* UI-REDESIGN Ergänzung (Aug 2026, Nachbesserung II):
                        Status-Badge pro Item, gesteuert von `_bucket` +
                        `mandant_decision`. Konsistente Ampel-Farblogik. */}
                    {r._bucket === "open" ? (
                      <span
                        className="ml-auto inline-flex items-center rounded-full bg-slate-100 text-slate-700 border border-slate-200 px-2 py-0.5 text-[10px] font-medium"
                        data-testid={`fsk-record-list-item-badge-${r.id}`}
                      >
                        Offen
                      </span>
                    ) : r._bucket === "decided" && r.mandant_decision === "freigegeben" ? (
                      <span
                        className="ml-auto inline-flex items-center rounded-full bg-emerald-100 text-emerald-700 px-2 py-0.5 text-[10px] font-medium"
                        data-testid={`fsk-record-list-item-badge-${r.id}`}
                      >
                        Freigegeben
                      </span>
                    ) : r._bucket === "decided" && r.mandant_decision === "abgelehnt" ? (
                      <span
                        className="ml-auto inline-flex items-center rounded-full bg-rose-100 text-rose-700 px-2 py-0.5 text-[10px] font-medium"
                        data-testid={`fsk-record-list-item-badge-${r.id}`}
                      >
                        Abgelehnt
                      </span>
                    ) : null}
                  </div>
                  {empId ? (
                    <div
                      className={
                        (active ? "text-slate-400" : "text-slate-400") +
                        " mt-0.5 text-[10px] font-mono truncate"
                      }
                      title="Mitarbeiter-ID (technische Nachvollziehbarkeit)"
                    >
                      {empId}
                    </div>
                  ) : null}
                  <div className={active ? "mt-0.5 text-xs text-slate-300" : "mt-0.5 text-xs text-slate-500"}>
                    {decidedLabel}
                  </div>
                  {/* Block G (Feb 2026 · §11.c/§4.5): Wiedervorlage-Datum
                      wird bei freigegebenen Records als kleine Info-Zeile in
                      der Listen-Karte gezeigt. */}
                  {r._bucket === "decided"
                    && r.mandant_decision === "freigegeben"
                    && r.wiedervorlage_faellig_am ? (
                    <div
                      className={active ? "mt-0.5 text-[11px] text-slate-300" : "mt-0.5 text-[11px] text-slate-500"}
                      data-testid={`fsk-record-list-item-due-${r.id}`}
                    >
                      Wiedervorlage: {new Date(r.wiedervorlage_faellig_am).toLocaleDateString("de-DE")}
                    </div>
                  ) : null}
                  {!r.has_back_photo ? (
                    <div className={active ? "mt-1 text-[11px] italic text-amber-200" : "mt-1 text-[11px] italic text-amber-700"}>
                      Alt-Datensatz — nur Vorderseite
                    </div>
                  ) : null}
                </button>
              );
            })
          )}
        </div>
        <div className="lg:col-span-2">
          {selected ? (
            <RecordDetail
              recordSummary={selected}
              onDecided={handleDecided}
            />
          ) : activeRecords.length > 0 ? (
            <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
              {DEFAULT_PLACEHOLDER}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
